import { Head } from 'vike-react/Head'
import { useConfig } from 'vike-react/useConfig'

interface ProductListSeoHeadProps {
  lang: string | undefined
  productTotal: number
  canonical: string
  search?: string
}

export default function ProductListSeoHead({ lang, productTotal, canonical, search }: ProductListSeoHeadProps) {
  const title = search
    ? `Zoekresultaten: "${search}" | Global Control Group Shop`
    : 'Alle producten | Global Control Group Shop'
  const description = search
    ? `${productTotal} ${productTotal === 1 ? 'resultaat' : 'resultaten'} voor "${search}" in de Global Control Group webshop.`
    : `Bekijk ${productTotal} industriële automatiseringsproducten van B&R, Siemens en meer in de Global Control Group webshop.`

  const configure = useConfig()
  configure({ title, description, lang: lang ?? 'nl' })

  const collectionLd = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: title,
    description,
    url: canonical,
    inLanguage: lang ?? 'nl',
  })

  const breadcrumbItems: Array<{ '@type': string; position: number; name: string; item?: string }> = [
    { '@type': 'ListItem', position: 1, name: 'Home', item: `/${lang ?? 'nl'}/` },
    { '@type': 'ListItem', position: 2, name: 'Producten', item: canonical },
  ]
  if (search) {
    breadcrumbItems.push({ '@type': 'ListItem', position: 3, name: `Zoekresultaten: "${search}"` })
  }

  const breadcrumbLd = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems,
  })

  return (
    <Head>
      <link rel="canonical" href={canonical} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <JsonLdScript json={collectionLd} />
      <JsonLdScript json={breadcrumbLd} />
    </Head>
  )
}

function JsonLdScript({ json }: { json: string }) {
  return (
    <script
      type="application/ld+json"
      {...{ dangerouslySetInnerHTML: { __html: json } }}
    />
  )
}
